import React, { useEffect, useCallback } from "react";
import { Edit } from "@refinedev/antd";
import { useOne, useUpdate, useNavigation, useInvalidate } from "@refinedev/core";
import { Form, Card, Space, Button, message, Spin, Alert } from "antd";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";
import { SourcingStepper } from "./components/SourcingStepper";
import { TypeScopeSelector } from "./components/TypeScopeSelector";
import { BidForm } from "./components/BidForm";
import { SourcingReviewStep } from "./components/SourcingReviewStep";
import { AttachmentsStep } from "./components/AttachmentsStep";
import { useCreateSourcing } from "./hooks/useCreateSourcing";
import { SOURCING_ONE_QUERY, UPDATE_SOURCING_MUTATION } from "./queries";
import { Sourcing, SourcingType, SourcingStatus, SourcingScope, TechnicalEvaluationMode } from "./interface";

export const SourcingEdit: React.FC = () => {
  const { goBack } = useNavigation();
  const [form] = Form.useForm();

  const { data, isLoading } = useOne<Sourcing>({
    resource: "sourcings",
    id: window.location.pathname.split('/').pop(),
    meta: { gqlQuery: SOURCING_ONE_QUERY },
  });

  const { mutate: updateSourcing } = useUpdate();
  const invalidate = useInvalidate();

  const {
    currentStep,
    formData,
    loading,
    updateFormData,
    updateBidDetails,
    nextStep,
    prevStep,
    goToStep,
    canProceedToNext,
  } = useCreateSourcing();

  // Load existing data
  useEffect(() => {
    if (data?.data && formData.prIds.length === 0) { // Only load if we haven't loaded data yet
      const sourcing = data.data;
      console.log('[DEBUG edit] Loading existing sourcing data:', { 
        approvalType: sourcing.approvalType, 
        steps: sourcing.steps, 
        externalWorkflow: sourcing.externalWorkflow 
      });
      if (sourcing.status !== SourcingStatus.DRAFT) {
        message.error("Only draft sourcings can be edited");
        goBack();
        return;
      }

      const updates: any = {
        prIds: sourcing.prIds,
        title: sourcing.title,
        description: sourcing.description,
        type: sourcing.type,
        sourcingScope: sourcing.sourcingScope,
        invitationMode: sourcing.invitationMode,
        items: sourcing.items,
        totalAmount: sourcing.totalAmount,
        currency: sourcing.currency,
        notes: sourcing.notes,
        attachments: sourcing.attachments,
        evaluationCriterias: sourcing.evaluationCriterias,
        technicalEvaluationMode: sourcing.technicalEvaluationMode,
        technicalEvaluationThreshold: sourcing.technicalEvaluationThreshold,
        approvalType: sourcing.approvalType as 'LOCAL' | 'EXTERNAL',
        steps: sourcing.steps,
        externalWorkflow: sourcing.externalWorkflow,
      };

      console.log('[DEBUG edit] Updates to formData:', updates);

      // If RFP and no evaluation criteria, add defaults
      if (sourcing.type === 'RFP' && (!sourcing.evaluationCriterias || sourcing.evaluationCriterias.length === 0)) {
        updates.evaluationCriterias = [
          {
            id: 'default-1',
            name: 'Technical Compliance',
            description: 'Conformance to technical specifications',
            weight: 30,
            visibleToSuppliers: 'FULL',
            type: 'TECHNICAL',
          },
          {
            id: 'default-2',
            name: 'Experience & References',
            description: 'Relevant experience and references',
            weight: 20,
            visibleToSuppliers: 'FULL',
            type: 'TECHNICAL',
          },
          {
            id: 'default-3',
            name: 'Delivery Time',
            description: 'Proposed delivery timeline',
            weight: 10,
            visibleToSuppliers: 'FULL',
            type: 'TECHNICAL',
          },
          {
            id: 'default-4',
            name: 'Financial (Price vs Est.)',
            description: 'Price competitiveness against estimate',
            weight: 40,
            visibleToSuppliers: 'FULL',
            type: 'FINANCIAL',
          },
        ];
      }

      updateFormData(updates);
    }
  }, [data?.data?.id, updateFormData]); // Use more specific dependency

  const steps = [
    {
      title: "Bid Details",
      description: formData.totalAmount ? `Configure items and details - Total: ${formData.totalAmount} ${formData.currency || ''}` : "Configure items and details",
      icon: undefined,
    },
    {
      title: "Source Type & Scope",
      description: formData.totalAmount ? `Select sourcing type and scope - Total: ${formData.totalAmount} ${formData.currency || ''}` : "Select sourcing type and scope",
      icon: undefined,
    },
    {
      title: "Additional Informations",
      description: formData.totalAmount ? `Upload supporting documents and evaluation criteria - Total: ${formData.totalAmount} ${formData.currency || ''}` : "Upload supporting documents and evaluation criteria",
      icon: undefined,
    },
    {
      title: "Review & Approval",
      description: formData.totalAmount ? `Review sourcing details and select approval flow - Total: ${formData.totalAmount} ${formData.currency || ''}` : "Review sourcing details and select approval flow",
      icon: undefined,
    },
  ];

  // Calculate completed steps based on form data
  const completedSteps = [
    formData.prIds.length > 0 && formData.items.length > 0,
    !!(formData.type && formData.sourcingScope),
    true, // Attachments are optional
    !!(formData.approvalType && ((formData.approvalType === 'EXTERNAL' && formData.externalWorkflow) || (formData.approvalType === 'LOCAL' && formData.steps && formData.steps.length > 0))),
  ];

  // Use the currentStep from the hook for navigation
  const effectiveCurrentStep = currentStep;

  const handleTypeChange = (type: SourcingType) => {
    updateFormData({ type });
  };

  const handleScopeChange = (sourcingScope: SourcingScope) => {
    updateFormData({ sourcingScope });
  };

  const handleTechnicalEvaluationModeChange = (mode: TechnicalEvaluationMode) => {
    updateFormData({ technicalEvaluationMode: mode });
  };

  const handleTechnicalEvaluationThresholdChange = (threshold: number | undefined) => {
    updateFormData({ technicalEvaluationThreshold: threshold });
  };


  const handlePrChange = useCallback((prIds: string[]) => {
    updateFormData({ prIds });
  }, [updateFormData]);

  const handleBidChange = useCallback((bidDetails: any) => {
    updateBidDetails(bidDetails);
  }, [updateBidDetails]);

  const handleApprovalChange = useCallback((approvalData: any) => {
    updateFormData({
      approvalType: approvalData.approvalType,
      steps: approvalData.steps,
      externalWorkflow: approvalData.externalWorkflow,
    });
  }, [updateFormData]);

  const handleSave = async () => {
    if (!data?.data?.id) return;

    const saveData = {
      title: formData.title,
      description: formData.description,
      type: formData.type,
      sourcingScope: formData.sourcingScope,
      invitationMode: formData.invitationMode,
      items: formData.items.map(item => {
        const { id,  ...rest } = item;
        return rest;
      }),
      totalAmount: formData.totalAmount,
      currency: formData.currency,
      notes: formData.notes,
      attachments: formData.attachments,
      evaluationCriterias: formData.evaluationCriterias?.map(criteria => {
        const { id,  ...rest } = criteria;
        return rest;
      }),
      technicalEvaluationMode: formData.technicalEvaluationMode,
      technicalEvaluationThreshold: formData.technicalEvaluationThreshold,
      approvalType: formData.approvalType,
      steps: formData.steps,
      externalWorkflow: formData.externalWorkflow,
    };

    console.log('[DEBUG edit] Saving sourcing with data:', {
      approvalType: saveData.approvalType,
      steps: saveData.steps,
      externalWorkflow: saveData.externalWorkflow
    });

    try {
      await updateSourcing({
        resource: "sourcings",
        id: data.data.id,
        values: saveData,
        meta: {
          gqlMutation: UPDATE_SOURCING_MUTATION,
        },
      });

      message.success("Sourcing updated successfully");
      
      // Invalidate the cache to ensure fresh data on next fetch
      invalidate({
        resource: "sourcings",
        invalidates: ["detail", "list"],
      });
      
      goBack();
    } catch (error: any) {
      message.error(error.message || "Failed to update sourcing");
    }
  };

  const renderFooterButtons = () => {
    const buttons = [];

    buttons.push(
      <Button key="save" onClick={handleSave} icon={<SaveOutlined />} loading={loading}>
        Save Changes
      </Button>
    );

    if (currentStep > 0) {
      buttons.push(
        <Button key="prev" onClick={prevStep}>
          Previous
        </Button>
      );
    }

    if (currentStep < 3) {
      buttons.push(
        <Button key="next" type="primary" onClick={nextStep} disabled={!canProceedToNext}>
          Next
        </Button>
      );
    }

    return <Space>{buttons}</Space>;
  };

  if (isLoading) {
    return <Spin size="large" />;
  }

  if (!data?.data) {
    return <div>Sourcing not found</div>;
  }

  const renderStepContent = () => {
    switch (effectiveCurrentStep) {
      case 0:
        return (
          <Card title="Step 1: Configure Bid Details" style={{ marginTop: 16 }}>
            {formData.totalAmount && (
              <Alert
                message={`Total Amount: ${formData.totalAmount} ${formData.currency || ''}`}
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}
            <BidForm
              value={{
                items: formData.items,
                totalAmount: formData.totalAmount,
                currency: formData.currency,
                notes: formData.notes,
                title: formData.title,
                description: formData.description
              }}
              onChange={handleBidChange}
              selectedPrIds={formData.prIds}
              onPrChange={handlePrChange}
            />
          </Card>
        );
      case 1:
        return (
          <Card title="Step 2: Select Source Type & Scope" style={{ marginTop: 16 }}>
            {formData.totalAmount && (
              <Alert
                message={`Total Amount: ${formData.totalAmount} ${formData.currency || ''}`}
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}
            <TypeScopeSelector
              typeValue={formData.type}
              scopeValue={formData.sourcingScope}
              technicalEvaluationMode={formData.technicalEvaluationMode}
              technicalEvaluationThreshold={formData.technicalEvaluationThreshold}
              onTypeChange={handleTypeChange}
              onScopeChange={handleScopeChange}
              onTechnicalEvaluationModeChange={handleTechnicalEvaluationModeChange}
              onTechnicalEvaluationThresholdChange={handleTechnicalEvaluationThresholdChange}
            />
          </Card>
        );
      case 2:
        return (
          <AttachmentsStep
            attachments={formData.attachments}
            onAttachmentsChange={(attachments) => {
              updateFormData({ attachments });
            }}
            type={formData.type}
            evaluationCriterias={formData.evaluationCriterias}
            onEvaluationCriteriasChange={(evaluationCriterias) => {
              updateFormData({ evaluationCriterias });
            }}
          />
        );
      case 3:
        return (
          <Card title="Step 4: Review & Approval" style={{ marginTop: 16 }}>
            <SourcingReviewStep
              formData={formData}
              onApprovalChange={handleApprovalChange}
            />
          </Card>
        );
      default:
        return null;
    }
  };

  return (
    <Edit
      title={`Edit Sourcing: ${data.data?.sourcingNo}`}
      headerProps={{
        extra: (
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={goBack}>
              Back
            </Button>
          </Space>
        ),
      }}
      footerButtons={renderFooterButtons()}
    >
      <Form form={form} layout="vertical">
        <SourcingStepper
          currentStep={effectiveCurrentStep}
          onStepChange={goToStep}
          completedSteps={completedSteps}
          steps={steps}
        />

        {renderStepContent()}
      </Form>
    </Edit>
  );
};